// MDM v3 (Wolfenstein skeletal mesh): surfaces whose vertices are weighted
// offsets from bones, and tags attached to one bone. The bones themselves
// (and all animation frames) live in an MDX file. Layout: qfiles.h,
// loading: R_LoadMDM (tr_model.c).

import { BinReader } from './bin';
import type { Axis } from './skeleton';
import type { Vec3 } from '../../shared/math';

export interface MdmSurface {
  name: string;
  /** Shader named in the file (used when the skin has no entry). */
  shader: string;
  numVerts: number;
  normals: Float32Array;
  st: Float32Array;
  /** Per vertex: first index into the weight arrays and weight count. */
  weightStart: Uint32Array;
  weightCount: Uint8Array;
  /** Global bone index of every weight. */
  weightBone: Uint16Array;
  weightValue: Float32Array;
  /** Offset from the bone, 3 floats per weight. */
  weightOffset: Float32Array;
  indexes: Uint32Array;
  /** Bones the vertices reference. */
  boneRefs: number[];
}

export interface MdmTag {
  name: string;
  axis: Axis;
  boneIndex: number;
  offset: Vec3;
  boneRefs: number[];
}

export interface MdmModel {
  name: string;
  surfaces: MdmSurface[];
  tags: MdmTag[];
}

export function parseMdm(data: Uint8Array, name = 'mdm'): MdmModel {
  const r = new BinReader(data);
  r.check('MDMW', [3], name);
  const numSurfaces = r.i32(80);
  const ofsSurfaces = r.i32(84);
  const numTags = r.i32(88);
  const ofsTags = r.i32(92);

  const surfaces: MdmSurface[] = [];
  let s = ofsSurfaces;
  for (let i = 0; i < numSurfaces; i++) {
    const numVerts = r.i32(s + 144);
    const ofsVerts = r.i32(s + 148);
    const numTriangles = r.i32(s + 152);
    const ofsTriangles = r.i32(s + 156);
    const numBoneRefs = r.i32(s + 164);
    const ofsBoneRefs = r.i32(s + 168);
    const ofsEnd = r.i32(s + 172);

    // count the weights first
    let total = 0;
    let p = s + ofsVerts;
    for (let v = 0; v < numVerts; v++) {
      const n = r.i32(p + 20);
      total += n;
      p += 24 + n * 20;
    }
    const surf: MdmSurface = {
      name: r.str(s + 4).toLowerCase(),
      shader: r.str(s + 68),
      numVerts,
      normals: new Float32Array(numVerts * 3),
      st: new Float32Array(numVerts * 2),
      weightStart: new Uint32Array(numVerts),
      weightCount: new Uint8Array(numVerts),
      weightBone: new Uint16Array(total),
      weightValue: new Float32Array(total),
      weightOffset: new Float32Array(total * 3),
      indexes: new Uint32Array(numTriangles * 3),
      boneRefs: [],
    };
    p = s + ofsVerts;
    let w = 0;
    for (let v = 0; v < numVerts; v++) {
      for (let k = 0; k < 3; k++) surf.normals[v * 3 + k] = r.f32(p + k * 4);
      surf.st[v * 2] = r.f32(p + 12);
      surf.st[v * 2 + 1] = r.f32(p + 16);
      const n = r.i32(p + 20);
      surf.weightStart[v] = w;
      surf.weightCount[v] = n;
      p += 24;
      for (let k = 0; k < n; k++, w++, p += 20) {
        surf.weightBone[w] = r.i32(p);
        surf.weightValue[w] = r.f32(p + 4);
        surf.weightOffset[w * 3] = r.f32(p + 8);
        surf.weightOffset[w * 3 + 1] = r.f32(p + 12);
        surf.weightOffset[w * 3 + 2] = r.f32(p + 16);
      }
    }
    for (let k = 0; k < numTriangles * 3; k++) surf.indexes[k] = r.i32(s + ofsTriangles + k * 4);
    for (let k = 0; k < numBoneRefs; k++) surf.boneRefs.push(r.i32(s + ofsBoneRefs + k * 4));
    surfaces.push(surf);
    s += ofsEnd;
  }

  const tags: MdmTag[] = [];
  let t = ofsTags;
  for (let i = 0; i < numTags; i++) {
    const numRefs = r.i32(t + 116);
    const ofsRefs = r.i32(t + 120);
    const boneRefs: number[] = [];
    for (let k = 0; k < numRefs; k++) boneRefs.push(r.i32(t + ofsRefs + k * 4));
    tags.push({
      name: r.str(t),
      axis: [r.vec(t + 64), r.vec(t + 76), r.vec(t + 88)],
      boneIndex: r.i32(t + 100),
      offset: r.vec(t + 104),
      boneRefs,
    });
    t += r.i32(t + 124);
  }
  return { name: r.str(8), surfaces, tags };
}
